// Alert (shadcn-style) — refatorado pra flex direto.
//
// A versão original (cva com grid `grid-cols-[auto_1fr]` + `col-start-2`
// nos filhos + seletor `[&>svg]`) tinha duas fragilidades:
//   1. Em alguns builds o JIT do Tailwind não compilava os arbitrary
//      values (`grid-cols-[auto_1fr]`), e o ícone empilhava acima do
//      título.
//   2. O seletor `[&>svg]` quebrava com lucide-react no Preact-compat.
//
// Esta versão usa um wrapper `<div role="alert">` com `flex items-start
// gap-3` em volta de `<div class="flex-1 min-w-0 space-y-1">` que recebe
// AlertTitle + AlertDescription. O ícone fica em outro flex item, sem
// depender de seletores arbitrários.
//
// API mantida: ainda funciona com `<Alert variant="destructive">{icon}
// <AlertTitle>...</AlertTitle><AlertDescription>...</AlertDescription>
// </Alert>` — internamente reagrupamos as crianças.

import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@app/ui/lib/utils"

const alertVariants = cva(
  "relative flex w-full items-start gap-3 rounded-lg border px-4 py-3 text-sm",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive:
          "border-destructive/50 text-destructive dark:border-destructive",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant, children, ...props }, ref) => {
    // Separa o primeiro filho (esperado: ícone) do restante (título/descrição)
    // pra manter a coluna direita com largura controlada (min-w-0 + flex-1).
    const arr = React.Children.toArray(children);
    let icon: React.ReactNode = null;
    let rest: React.ReactNode[] = arr;
    if (arr.length > 0 && React.isValidElement(arr[0])) {
      const first = arr[0] as React.ReactElement<{ className?: string }>;
      const firstProps = (first.props ?? {}) as { className?: string };
      // Heurística: se o primeiro filho é um <svg> (lucide), usa como ícone.
      // Caso o caller queira tratar manualmente, basta passar todos como
      // children dentro de um wrapper.
      const t = first.type as any;
      const isSvg =
        first.props && (
          t === "svg" ||
          (typeof t === "function" && (t.displayName || t.name || "").toLowerCase().includes("icon"))
        );
      // Fallback: aceita qualquer elemento com prop `className` contendo
      // `size-` (lucide convention) como ícone também.
      const looksLikeIcon =
        isSvg ||
        (typeof firstProps.className === "string" &&
          /\bsize-\d+\b/.test(firstProps.className));
      if (looksLikeIcon) {
        icon = first;
        rest = arr.slice(1);
      }
    }
    return (
      <div
        ref={ref}
        role="alert"
        className={cn(alertVariants({ variant }), className)}
        {...props}
      >
        {icon && (
          <span class="mt-0.5 flex shrink-0 items-center justify-center [&>svg]:size-4">
            {icon}
          </span>
        )}
        <div class="min-w-0 flex-1 space-y-1">{rest}</div>
      </div>
    );
  }
)
Alert.displayName = "Alert"

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn(
      "min-w-0 font-medium leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("min-w-0 text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
))
AlertDescription.displayName = "AlertDescription"

export { Alert, AlertTitle, AlertDescription }
